/**
 * Category List Page
 *
 * Implements: US-M5-003 (Manage Categories)
 * AC-166: Category list shows hierarchy (parent → sub-category)
 * AC-169: Reorder categories via drag-drop (simplified with buttons)
 * AC-170: Activate/Deactivate categories
 * AC-171: Cannot delete category with products
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  IndexTable,
  Text,
  Badge,
  Button,
  InlineStack,
  Box,
  Spinner,
  EmptyState,
  Modal,
  BlockStack,
  Thumbnail,
  Icon,
} from '@shopify/polaris';
import { PlusIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import { categoryService, type Category } from '../../services/category.service';

export function CategoryListPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Load categories
  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await categoryService.list();
      setCategories(result);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Handlers
  const handleToggleStatus = useCallback(async (category: Category) => {
    try {
      await categoryService.toggleStatus(category.id);
      loadCategories();
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  }, [loadCategories]);

  const handleDeleteClick = useCallback((category: Category) => {
    setCategoryToDelete(category);
    setDeleteError('');
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!categoryToDelete) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await categoryService.delete(categoryToDelete.id);
      setDeleteModalOpen(false);
      setCategoryToDelete(null);
      loadCategories();
    } catch (error: any) {
      console.error('Failed to delete category:', error);
      setDeleteError(
        error.response?.data?.message ||
        'Cannot delete category. Make sure it has no products or sub-categories.'
      );
    } finally {
      setDeleting(false);
    }
  }, [categoryToDelete, loadCategories]);

  const handleMoveUp = useCallback(async (category: Category) => {
    const sameLevel = categories.filter((c) => c.parentId === category.parentId);
    const currentIndex = sameLevel.findIndex((c) => c.id === category.id);
    if (currentIndex <= 0) return;

    const orders = sameLevel.map((c, i) => {
      if (i === currentIndex - 1) return { id: c.id, displayOrder: currentIndex };
      if (i === currentIndex) return { id: c.id, displayOrder: currentIndex - 1 };
      return { id: c.id, displayOrder: i };
    });

    try {
      await categoryService.reorder(orders);
      loadCategories();
    } catch (error) {
      console.error('Failed to reorder:', error);
    }
  }, [categories, loadCategories]);

  const handleMoveDown = useCallback(async (category: Category) => {
    const sameLevel = categories.filter((c) => c.parentId === category.parentId);
    const currentIndex = sameLevel.findIndex((c) => c.id === category.id);
    if (currentIndex >= sameLevel.length - 1) return;

    const orders = sameLevel.map((c, i) => {
      if (i === currentIndex) return { id: c.id, displayOrder: currentIndex + 1 };
      if (i === currentIndex + 1) return { id: c.id, displayOrder: currentIndex };
      return { id: c.id, displayOrder: i };
    });

    try {
      await categoryService.reorder(orders);
      loadCategories();
    } catch (error) {
      console.error('Failed to reorder:', error);
    }
  }, [categories, loadCategories]);

  // Flatten categories for display with indentation
  const flattenCategories = (cats: Category[], level: number = 0): Array<Category & { level: number }> => {
    const result: Array<Category & { level: number }> = [];
    for (const cat of cats) {
      result.push({ ...cat, level });
      if (cat.children && cat.children.length > 0) {
        result.push(...flattenCategories(cat.children, level + 1));
      }
    }
    return result;
  };

  const flatCategories = flattenCategories(categories.filter((c) => !c.parentId));

  const rowMarkup = flatCategories.map((category, index) => (
    <IndexTable.Row
      id={category.id}
      key={category.id}
      position={index}
    >
      <IndexTable.Cell>
        <InlineStack gap="200" blockAlign="center">
          {category.level > 0 && (
            <Box paddingInlineStart={`${category.level * 400}` as any}>
              <Icon source={ChevronRightIcon} tone="subdued" />
            </Box>
          )}
          <InlineStack gap="300" blockAlign="center">
            {category.bannerUrl && (
              <Thumbnail source={category.bannerUrl} alt={category.name} size="small" />
            )}
            <BlockStack gap="100">
              <Text variant="bodyMd" fontWeight="semibold" as="span">
                {category.name}
              </Text>
              <Text variant="bodySm" tone="subdued" as="span">
                {category.slug}
              </Text>
            </BlockStack>
          </InlineStack>
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {category.productCount} products
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={category.status === 'ACTIVE' ? 'success' : 'critical'}>
          {category.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd">
          {category.displayOrder}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          <Button size="slim" onClick={() => handleMoveUp(category)}>
            ↑
          </Button>
          <Button size="slim" onClick={() => handleMoveDown(category)}>
            ↓
          </Button>
          <Button size="slim" onClick={() => navigate(`/categories/${category.id}`)}>
            Edit
          </Button>
          <Button
            size="slim"
            tone={category.status === 'ACTIVE' ? 'critical' : 'success'}
            onClick={() => handleToggleStatus(category)}
          >
            {category.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            size="slim"
            tone="critical"
            onClick={() => handleDeleteClick(category)}
            disabled={category.productCount > 0 || (category.children?.length ?? 0) > 0}
          >
            Delete
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  const emptyState = (
    <EmptyState
      heading="No categories found"
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      action={{
        content: 'Add category',
        onAction: () => navigate('/categories/new'),
      }}
    >
      <p>Add categories to organize your products.</p>
    </EmptyState>
  );

  return (
    <Page
      title="Categories"
      primaryAction={{
        content: 'Add category',
        icon: PlusIcon,
        onAction: () => navigate('/categories/new'),
      }}
    >
      <Card padding="0">
        {loading ? (
          <Box padding="600">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        ) : flatCategories.length === 0 ? (
          emptyState
        ) : (
          <IndexTable
            itemCount={flatCategories.length}
            headings={[
              { title: 'Category' },
              { title: 'Products' },
              { title: 'Status' },
              { title: 'Order' },
              { title: 'Actions' },
            ]}
            selectable={false}
          >
            {rowMarkup}
          </IndexTable>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete category?"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          loading: deleting,
          onAction: handleDeleteConfirm,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDeleteModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p">
              Are you sure you want to delete "{categoryToDelete?.name}"?
            </Text>
            {deleteError && (
              <Text as="p" tone="critical">
                {deleteError}
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
